<template>
  <div class="users-page space-y-5">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Users</h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
          {{ users.length }} user{{ users.length !== 1 ? 's' : '' }}
        </p>
      </div>
      <button
        @click="openCreateModal"
        class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        <span class="material-icons text-[18px]" aria-hidden="true">person_add</span>
        Add User
      </button>
    </div>

    <!-- Stale-data refresh failure -->
    <AlertBanner
      v-if="fetchError && users.length"
      type="error"
      message="Couldn't refresh users — showing the last loaded list."
      :dismissible="false"
      class="mb-4"
    />

    <!-- Fetch failure with nothing loaded -->
    <div v-if="fetchError && !users.length" class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700">
      <ErrorState title="Couldn't load users" :message="fetchError" @retry="fetchUsers" />
    </div>

    <!-- Table -->
    <DataTable
      v-else
      :data="users"
      :columns="tableColumns"
      :loading="isLoading"
      :searchable="true"
      :pagination="true"
      :clickable="true"
      loading-text="Loading users..."
      empty-text="No users found"
      @row-click="goToUser"
    >
      <template #cell-displayName="{ row }">
        <router-link
          :to="{ name: 'UserView', params: { id: row.uid } }"
          class="inline-flex items-center gap-2 group/link"
          @click.stop
        >
          <div class="w-7 h-7 rounded-full bg-secondary-50 dark:bg-secondary-900/20 flex items-center justify-center flex-shrink-0">
            <span class="text-xs font-bold text-secondary-700 dark:text-secondary-300">
              {{ row.displayName?.charAt(0)?.toUpperCase() }}
            </span>
          </div>
          <span class="text-sm font-medium text-neutral-900 dark:text-neutral-100 group-hover/link:text-primary-600 dark:group-hover/link:text-primary-400 transition-colors">
            {{ row.displayName }}
          </span>
        </router-link>
      </template>

      <template #cell-email="{ row }">
        <span class="text-sm text-neutral-600 dark:text-neutral-400">{{ row.email }}</span>
      </template>

      <template #cell-userRole="{ row }">
        <span
          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
          :class="{
            'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300': row.userRole === 'ADMIN',
            'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300': row.userRole === 'USER',
            'bg-neutral-200 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400': !['ADMIN','USER'].includes(row.userRole)
          }"
        >
          <span class="inline-block w-1.5 h-1.5 rounded-full" :class="{
            'bg-violet-500': row.userRole === 'ADMIN',
            'bg-blue-500': row.userRole === 'USER',
            'bg-neutral-400': !['ADMIN','USER'].includes(row.userRole)
          }"></span>
          {{ row.userRole }}
        </span>
      </template>

      <template #cell-sitesCount="{ row }">
        <span class="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">{{ row.sites ? row.sites.length : 0 }}</span>
      </template>

      <template #cell-createdAt="{ row }">
        <span class="text-sm text-neutral-500 dark:text-neutral-400">
          {{ row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—' }}
        </span>
      </template>

      <template #actions="{ row }">
        <div class="flex items-center gap-1 justify-center">
          <router-link
            :to="{ name: 'UserView', params: { id: row.uid } }"
            class="p-2 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/30 transition-all duration-150"
            title="Edit user"
            aria-label="Edit user"
            @click.stop
          >
            <span class="material-icons text-[18px]" aria-hidden="true">edit</span>
          </router-link>
          <button
            @click.stop="confirmDeleteUser(row)"
            class="p-2 rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/30 transition-all duration-150"
            title="Delete user"
            aria-label="Delete user"
          >
            <span class="material-icons text-[18px]" aria-hidden="true">delete_outline</span>
          </button>
        </div>
      </template>
    </DataTable>

    <!-- Create User Modal -->
    <Modal :show="showCreateModal" title="Create New User" size="lg" @close="closeCreateModal">
      <form id="create-user-form" @submit.prevent="handleCreate" class="space-y-4">
        <div>
          <label for="create-user-name" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Display Name</label>
          <input id="create-user-name" v-model="form.displayName" type="text" required placeholder="Full name"
            class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label for="create-user-email" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Email</label>
            <input id="create-user-email" v-model="form.email" type="email" required placeholder="user@example.com"
              class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
          </div>
          <div>
            <label for="create-user-password" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Password</label>
            <div class="relative">
              <input id="create-user-password" v-model="form.password" :type="showPassword ? 'text' : 'password'" required
                minlength="6" autocomplete="new-password" placeholder="Min 6 characters"
                class="w-full px-3 py-2 pr-9 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
              <button
                type="button"
                @click="showPassword = !showPassword"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                class="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
              >
                <span class="material-icons text-[18px]" aria-hidden="true">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label for="create-user-role" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Role</label>
            <select id="create-user-role" v-model="form.userRole"
              class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer">
              <option value="ADMIN">Admin</option>
              <option value="USER">User</option>
            </select>
          </div>
          <div>
            <span class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Sites</span>
            <div class="space-y-1 max-h-28 overflow-y-auto border border-neutral-200 dark:border-neutral-600 rounded-lg p-2">
              <label v-for="site in allSites" :key="site.siteId" class="flex items-center gap-2 cursor-pointer px-2 py-1 rounded hover:bg-neutral-50 dark:hover:bg-neutral-700">
                <input type="checkbox" :value="site" v-model="form.sites"
                  class="w-3.5 h-3.5 text-primary-600 rounded border-neutral-300 dark:border-neutral-600 focus:ring-primary-500" />
                <span class="text-xs text-neutral-700 dark:text-neutral-300">{{ site.siteName || site.sitename }}</span>
              </label>
            </div>
          </div>
        </div>

        <div v-if="formError" role="alert" class="flex items-center gap-2 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg">
          <span class="material-icons text-red-500 text-sm" aria-hidden="true">error</span>
          <p class="text-xs text-red-700 dark:text-red-400">{{ formError }}</p>
        </div>
      </form>

      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <button @click="closeCreateModal"
            class="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 transition-colors">
            Cancel
          </button>
          <button type="submit" form="create-user-form" :disabled="isSubmitting"
            class="px-4 py-2 text-sm font-semibold text-white bg-primary-700 rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {{ isSubmitting ? 'Creating...' : 'Create User' }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Delete Confirmation -->
    <Modal :show="showDeleteConfirm" title="Delete User" size="sm" @close="closeDeleteModal">
      <div class="bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800/40 rounded-lg p-4 space-y-2">
        <p class="text-sm text-red-700 dark:text-red-300">
          Are you sure you want to delete <strong>{{ userToDelete?.displayName }}</strong>?
        </p>
        <p class="text-xs text-red-500 dark:text-red-400">
          This will permanently remove the user from Firebase Auth and Firestore.
        </p>
      </div>
      <div v-if="deleteError" role="alert" class="mt-3 flex items-center gap-2 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg">
        <span class="material-icons text-red-500 text-sm" aria-hidden="true">error</span>
        <p class="text-xs text-red-700 dark:text-red-400">{{ deleteError }}</p>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <button @click="closeDeleteModal"
            class="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 transition-colors">
            Cancel
          </button>
          <button @click="handleDelete" :disabled="isSubmitting"
            class="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {{ isSubmitting ? 'Deleting...' : 'Delete User' }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import axios from "axios";
import getCollection from "@/composables/getCollection";
import useToast from "@/composables/useToast";
import DataTable from "@/components/ui/DataTable.vue";
import Modal from "@/components/ui/Modal.vue";
import ErrorState from "@/components/ui/ErrorState.vue";
import AlertBanner from "@/components/ui/AlertBanner.vue";

export default {
  name: "UsersView",
  components: { DataTable, Modal, ErrorState, AlertBanner },
  setup() {
    const store = useStore();
    const router = useRouter();
    const apiUrl = store.state.apiUrl;
    store.commit("setCurrentRoute", "Users");

    const { showToast } = useToast();

    const users = ref([]);
    const isLoading = ref(false);
    const fetchError = ref(null);
    const showCreateModal = ref(false);
    const showDeleteConfirm = ref(false);
    const isSubmitting = ref(false);
    const formError = ref(null);
    const deleteError = ref(null);
    const userToDelete = ref(null);
    const showPassword = ref(false);

    const { documents: siteDocs } = getCollection("sites");

    const allSites = computed(() => {
      if (!siteDocs.value) return [];
      return siteDocs.value.map((s) => ({
        siteId: s.siteid || s.siteId || s.id,
        siteName: s.sitename || s.siteName || s.name || s.id,
      }));
    });

    const form = ref({
      displayName: "",
      email: "",
      password: "",
      userRole: "USER",
      sites: [],
    });

    const tableColumns = [
      { key: "displayName", label: "Name", sortable: true },
      { key: "email", label: "Email", sortable: true },
      { key: "userRole", label: "Role", sortable: true, align: "center" },
      { key: "sitesCount", label: "Sites", sortable: true, align: "center" },
      { key: "createdAt", label: "Created", sortable: true },
    ];

    const fetchUsers = async () => {
      isLoading.value = true;
      try {
        const response = await axios.get(`${apiUrl}/users`);
        users.value = response.data;
        fetchError.value = null;
      } catch (error) {
        console.error("Error fetching users:", error);
        fetchError.value = error.response?.data?.detail || error.message || "Request failed.";
      } finally {
        isLoading.value = false;
      }
    };

    const goToUser = (row) => {
      router.push({ name: "UserView", params: { id: row.uid } });
    };

    const openCreateModal = () => {
      formError.value = null;
      showPassword.value = false;
      form.value = { displayName: "", email: "", password: "", userRole: "USER", sites: [] };
      showCreateModal.value = true;
    };

    const closeCreateModal = () => {
      showCreateModal.value = false;
      formError.value = null;
    };

    const handleCreate = async () => {
      if (isSubmitting.value) return;
      isSubmitting.value = true;
      formError.value = null;
      try {
        const sites = form.value.sites.map((s) => ({
          siteId: s.siteId,
          siteName: s.siteName,
        }));
        await axios.post(`${apiUrl}/users`, {
          email: form.value.email,
          password: form.value.password,
          displayName: form.value.displayName,
          userRole: form.value.userRole,
          sites,
        });
        closeCreateModal();
        showToast("success", "User created");
        await fetchUsers();
      } catch (error) {
        formError.value = error.response?.data?.detail || "An error occurred. Please try again.";
        showToast("error", "Failed to create user. Please try again.");
      } finally {
        isSubmitting.value = false;
      }
    };

    const confirmDeleteUser = (user) => {
      userToDelete.value = user;
      deleteError.value = null;
      showDeleteConfirm.value = true;
    };

    const closeDeleteModal = () => {
      showDeleteConfirm.value = false;
      deleteError.value = null;
    };

    const handleDelete = async () => {
      if (!userToDelete.value || isSubmitting.value) return;
      isSubmitting.value = true;
      deleteError.value = null;
      try {
        await axios.delete(`${apiUrl}/users/${userToDelete.value.uid}`);
        showDeleteConfirm.value = false;
        userToDelete.value = null;
        showToast("success", "User deleted");
        await fetchUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        deleteError.value = error.response?.data?.detail || "Could not delete this user.";
        showToast("error", "Failed to delete user. Please try again.");
      } finally {
        isSubmitting.value = false;
      }
    };

    onMounted(fetchUsers);

    return {
      users, isLoading, fetchError, tableColumns, showCreateModal, showDeleteConfirm,
      isSubmitting, formError, deleteError, form, allSites, userToDelete, showPassword,
      goToUser, openCreateModal, closeCreateModal, closeDeleteModal, handleCreate,
      confirmDeleteUser, handleDelete, fetchUsers,
    };
  },
};
</script>

<style scoped>
.users-page > * {
  animation: fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;
}
.users-page > *:nth-child(1) { animation-delay: 0s; }
.users-page > *:nth-child(2) { animation-delay: 0.06s; }

@media (prefers-reduced-motion: reduce) {
  .users-page > * {
    animation: none;
  }
}

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
